import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type BackfillHistoricalAck,
  type BackfillHistoricalInput,
  type BackfillJobState,
  type CreateSnapshotInput,
  type ListSnapshotsFilters,
  type NetworthHistory,
  type NetworthPreview,
  type NetworthSummary,
  type SnapshotDetail,
  type SnapshotDrillResponse,
  type SnapshotList,
  networthApi,
} from "../lib/networth";

const baseKey = ["networth"] as const;

export function useNetworthSnapshots(filters: ListSnapshotsFilters = {}) {
  return useQuery<SnapshotList>({
    queryKey: [...baseKey, "list", filters],
    queryFn: () => networthApi.listSnapshots(filters),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useNetworthSnapshotDetail(stichtag: string) {
  return useQuery<SnapshotDetail>({
    queryKey: [...baseKey, "detail", stichtag],
    queryFn: () => networthApi.getSnapshotDetail(stichtag),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(stichtag),
  });
}

export function useNetworthSummary() {
  return useQuery<NetworthSummary>({
    queryKey: [...baseKey, "summary"],
    queryFn: () => networthApi.getSummary(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useNetworthPreview(stichtag: string | undefined) {
  return useQuery<NetworthPreview>({
    queryKey: [...baseKey, "preview", stichtag ?? "none"],
    queryFn: () => networthApi.getPreview(stichtag ?? ""),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(stichtag),
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: baseKey });
}

export function useCreateSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSnapshotInput) => networthApi.createSnapshot(input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stichtag: string) => networthApi.deleteSnapshot(stichtag),
    onSuccess: () => invalidateAll(qc),
  });
}

export type NetworthPeriod = "1M" | "3M" | "6M" | "YTD" | "1J" | "3J" | "5J" | "all";

function periodToRange(period: NetworthPeriod): { from?: string; to?: string } {
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  if (period === "all") return { to: todayStr };
  if (period === "YTD") {
    return {
      from: `${today.getUTCFullYear()}-01-01`,
      to: todayStr,
    };
  }
  const daysBack =
    period === "1M"
      ? 30
      : period === "3M"
        ? 90
        : period === "6M"
          ? 182
          : period === "1J"
            ? 365
            : period === "3J"
              ? 365 * 3
              : 365 * 5; // 5J
  const fromDate = new Date(today.getTime() - daysBack * 24 * 60 * 60 * 1000);
  return { from: fromDate.toISOString().slice(0, 10), to: todayStr };
}

export function useNetworthHistory(period: NetworthPeriod) {
  const range = periodToRange(period);
  return useQuery<NetworthHistory>({
    queryKey: [...baseKey, "history", period],
    queryFn: () => networthApi.getHistory(range),
    staleTime: 60 * 60 * 1000, // 1h — Snapshots ändern sich nur nachts
  });
}

export function useBackfillHistorical() {
  const qc = useQueryClient();
  return useMutation<BackfillHistoricalAck, Error, BackfillHistoricalInput>({
    mutationFn: (input) => networthApi.backfillHistorical(input),
    onSuccess: () => {
      // Status-Query sofort neu holen — sonst sieht der Hook noch den alten
      // idle-Wert und das refetchInterval springt nie auf 2s an.
      void qc.invalidateQueries({ queryKey: [...baseKey, "backfill-status"] });
    },
  });
}

export function useBackfillStatus(enabled: boolean) {
  const qc = useQueryClient();
  return useQuery<BackfillJobState>({
    queryKey: [...baseKey, "backfill-status"],
    queryFn: async () => {
      const state = await networthApi.getBackfillStatus();
      if (state.status === "completed") {
        // Job ist gerade fertig geworden — gezielt die Listen/Summary-Queries
        // dropen (nicht baseKey, sonst invalidieren wir uns selbst und loopen).
        void qc.invalidateQueries({ queryKey: [...baseKey, "list"] });
        void qc.invalidateQueries({ queryKey: [...baseKey, "summary"] });
        void qc.invalidateQueries({ queryKey: [...baseKey, "history"] });
        void qc.invalidateQueries({ queryKey: ["allocation"] });
      }
      return state;
    },
    enabled,
    // Aggressives Polling waehrend der Job laeuft. Wenn idle: zur Sicherheit
    // alle 10s nachschauen, sonst verpassen wir externe Status-Wechsel
    // (Cron-Run, anderer Browser-Tab) komplett.
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data?.status === "running") return 2000;
      return false;
    },
    staleTime: 0,
  });
}

export function useCleanupZeroSnapshots() {
  const qc = useQueryClient();
  return useMutation<{ deletedDays: number }, Error, void>({
    mutationFn: () => networthApi.cleanupZero(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: baseKey });
    },
  });
}


export function useSnapshotDrill(stichtag: string | null) {
  return useQuery<SnapshotDrillResponse>({
    queryKey: [...baseKey, "snapshot-drill", stichtag],
    queryFn: () => networthApi.getSnapshotDrill(stichtag as string),
    enabled: !!stichtag,
    staleTime: 60 * 60 * 1000,
  });
}
